/**
 * Message Requests section (#3039).
 *
 * Drives the Primary/Requests inbox split UI:
 *   - entry banner: opens the dedicated full Requests view.
 *   - full "See all" view: fetched into the PM threads pane as an overlay. Rows
 *     carry no control at rest. Report and Delete reveal on hover, on
 *     focus-within, or by swipe, and the row body opens the conversation.
 *   - decision bar: in an opened request, Delete and Accept in the compose box's
 *     slot. Accepting promotes the thread in place, with no page load.
 * Select mode lives in pm/requests-select.js, the deferred-commit undo in
 * pm/requests-undo.js, and cross-device sync in pm/requests-live.js. All three
 * drive the list through the PmRequests API exposed at the bottom of this file.
 * Actions call the /v1 REST endpoints through window.ApiClient.
 * Reading a Request thread sends no read receipt (suppressed server-side).
 */
(function () {
  'use strict';

  /**
   * Server-rendered Requests view, read from the page rather than spelled out
   * here: the idle prefetch, the cold open, the live reload and the keyset
   * pages all fetch it, and LinksHelper owns the path.
   */
  function viewUrl() {
    var main = document.getElementById('threads_main');
    return main ? (main.getAttribute('data-requests-view-url') || '') : '';
  }

  /**
   * Whether a fetched body is the fragment that was asked for. A lapsed
   * session answers with the landing page at status 200, because fetch
   * follows the redirect, and that page would replace the thread pane.
   * An empty body is a legitimate answer and passes.
   */
  function isFragment(html, marker) {
    var s = (html || '').trim();
    return !s || s.indexOf(marker) !== -1;
  }

  var VIEW_MARKER = 'message-requests-view-inner';
  var ROWS_MARKER = 'message-request-row';

  function section() {
    return document.getElementById('message-requests-section');
  }

  function fullView() {
    return document.getElementById('message-requests-view');
  }

  function t(key, fallback, params) {
    if (typeof window.t === 'function') {
      var s = window.t(key, params);
      if (s && s !== key) {
        return s;
      }
    }
    return fallback;
  }

  /** Disable the decision bar's controls while its request is in flight. */
  function setBarBusy(bar, busy) {
    if (!bar) return;
    var btns = bar.querySelectorAll('button');
    for (var i = 0; i < btns.length; i++) btns[i].disabled = busy;
    bar.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /**
   * POST through the ApiClient singleton. `options` is merged into the fetch
   * init, so a caller can pass a body or `keepalive: true`. ApiClient reads the
   * JWT synchronously, which is what makes it usable from a page-hide handler.
   */
  function apiPost(path, options) {
    if (!window.ApiClient || typeof window.ApiClient.request !== 'function') {
      return Promise.reject(new Error('ApiClient unavailable'));
    }
    var init = { method: 'POST' };
    if (options) {
      for (var k in options) {
        if (Object.prototype.hasOwnProperty.call(options, k)) init[k] = options[k];
      }
    }
    return window.ApiClient.request(path, init).then(function (resp) {
      if (!resp || !resp.ok) {
        var err = new Error('Request failed: ' + (resp ? resp.status : 'no response'));
        // Callers tell a refusal from a fault: a decline the server rejects
        // because the thread already left the queue is not worth undoing.
        err.status = resp ? resp.status : 0;
        throw err;
      }
      return resp;
    });
  }

  function actionError() {
    var msg = t('common.pm.requests_action_error', 'Could not complete that action. Please try again.');
    if (typeof window.showError === 'function') {
      window.showError(msg);
    } else {
      alert(msg);
    }
  }

  // Request rows are not .pm_thread rows, so view_thread (which reads thread
  // metadata from the #thread_<id> DOM row) can't drive them directly. Inject a
  // hidden stub row carrying the data update_user_header needs, then reuse
  // view_thread for an in-page open (messages load by AJAX, no full reload).
  function ensureThreadStub(row) {
    var id = row.getAttribute('data-thread-id');
    if (!id || document.getElementById('thread_' + id)) return;
    var stub = document.createElement('div');
    stub.id = 'thread_' + id;
    stub.className = 'pm_thread hidden';
    stub.setAttribute('data-thread-id', id);
    stub.setAttribute('data-uid', row.getAttribute('data-uid') || '');
    stub.setAttribute('data-name', row.getAttribute('data-name') || '');
    stub.setAttribute('data-username', row.getAttribute('data-username') || '');
    stub.setAttribute('data-thumb', row.getAttribute('data-thumb') || '');
    stub.setAttribute('data-profile-link', row.getAttribute('data-profile-link') || '');
    stub.setAttribute(
      'data-contact-badge-status',
      row.getAttribute('data-contact-badge-status') || 'active'
    );
    stub.setAttribute('data-contact-status', row.getAttribute('data-contact-status') || '');
    stub.setAttribute('data-inactive', row.getAttribute('data-inactive') || '');
    carrier().appendChild(stub);
  }

  /**
   * Host for a row that stands in for the open conversation without being a
   * Primary row. Outside #threads_left, for the reasons pm/index.twig gives:
   * .pm_thread descendants of that node are counted as the Primary list's
   * length, and the poll's full rebuild empties it.
   */
  function carrier() {
    var host = document.getElementById('pm-active-request-row');
    if (host) return host;
    host = document.createElement('div');
    host.id = 'pm-active-request-row';
    host.className = 'hidden';
    var panel = document.getElementById('threads_left_container');
    (panel || document.body).appendChild(host);
    return host;
  }

  // The stub stands in for a Primary row the peeked request does not have. Once
  // the request leaves the queue it stands for nothing, and the poll's row
  // reconcile would otherwise keep sorting it.
  //
  // The direct-link row carries no `hidden` of its own: the class is on its
  // #pm-active-request-row wrapper (pm/index.twig), so that whole node goes.
  function removeThreadStub(id) {
    var stub = document.getElementById('thread_' + id);
    if (!stub) return;
    var host = stub.closest('#pm-active-request-row');
    if (host) {
      // The row, not the host: a direct-link row and a later peek stub can
      // both be carried, and only one of them is leaving.
      stub.remove();
      if (!host.children.length) host.remove();
      return;
    }
    if (stub.classList.contains('hidden')) stub.remove();
  }

  // ───────────────────── conversation overflow menu ─────────────────────

  /**
   * Match the conversation overflow menu to the open conversation. A pending
   * Request is triaged by the decision bar, so its menu offers View profile,
   * Block and Report instead of the Primary thread actions. Reads the bar
   * rather than a flag of its own, so every path that shows or hides the bar
   * lands on the right menu.
   */
  function syncThreadMenu() {
    var bar = document.getElementById('message-request-bar');
    var isRequest = !!(bar && !bar.classList.contains('hidden'));
    var primary = document.getElementById('pm-menu-primary-actions');
    var request = document.getElementById('pm-menu-request-actions');
    if (primary) primary.classList.toggle('hidden', isRequest);
    if (request) request.classList.toggle('hidden', !isRequest);
    if (!isRequest) return;
    var profile = document.getElementById('pm-menu-view-profile');
    var link = document.getElementById('current_thread_link');
    if (profile) profile.setAttribute('href', (link && link.getAttribute('href')) || '#');
  }

  // Show the request decision bar for the open conversation and hide the
  // compose box (no replying until the request is accepted).
  function showRequestBar(id, uid, name) {
    var bar = document.getElementById('message-request-bar');
    if (bar) {
      bar.setAttribute('data-thread-id', id);
      // Written even when empty: whatever is left from the last request names
      // the wrong sender in the undo toast and the accepted line.
      bar.setAttribute('data-uid', uid || '');
      bar.setAttribute('data-name', name || '');
      setBarBusy(bar, false);
      bar.classList.remove('hidden');
      if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
        window.LucideIcons.initialize(bar);
      }
    }
    var compose = document.getElementById('threads_right_bottom');
    if (compose) compose.classList.add('hidden');
    // Gate per-message react/reply until the request is accepted (no read
    // receipt). Report/delete stay available. CSS lives in pm/index.twig.
    var tr = document.getElementById('threads_right');
    if (tr) tr.classList.add('pm-request-readonly');
    syncThreadMenu();
  }

  function hideRequestBar() {
    var bar = document.getElementById('message-request-bar');
    if (bar) {
      setBarBusy(bar, false);
      bar.classList.add('hidden');
    }
    syncThreadMenu();
  }

  /** Display name of the open request's sender, for copy that names them. */
  function openRequestName(bar) {
    var name = bar ? (bar.getAttribute('data-name') || '') : '';
    if (name) return name;
    var header = document.getElementById('current_thread_username');
    return header ? header.textContent.trim() : '';
  }

  // Mark which row the conversation pane is showing. The row styles itself off
  // data-open (accent bar + inset ring).
  function markOpenRow(row) {
    var list = document.getElementById('message-requests-view-list');
    if (!list) return;
    var prev = list.querySelectorAll('.message-request-row[data-open="true"]');
    for (var i = 0; i < prev.length; i++) {
      prev[i].removeAttribute('data-open');
      var was = prev[i].querySelector('.message-request-open');
      if (was) was.removeAttribute('aria-current');
    }
    if (!row) return;
    row.setAttribute('data-open', 'true');
    // The accent bar and the ring say this in colour alone. aria-current is
    // what carries it to assistive tech.
    var opener = row.querySelector('.message-request-open');
    if (opener) opener.setAttribute('aria-current', 'true');
  }

  // Open a request conversation in-page (no full reload). Reading a Request
  // thread sends no read receipt (suppressed server-side).
  function openRequestPeek(row) {
    var id = row.getAttribute('data-thread-id');
    if (!id) return;
    if (typeof window.view_thread !== 'function') {
      window.location.href = '/app/messages?thread_id=' + encodeURIComponent(id);
      return;
    }
    markOpenRow(row);
    ensureThreadStub(row);
    if (window.htm5_history_supported && window.history && window.history.pushState) {
      window.history.pushState(
        { thread_id: id },
        null,
        '/app/messages?thread_id=' + encodeURIComponent(id)
      );
    }
    window.view_thread(id, true);
    showRequestBar(id, row.getAttribute('data-uid'), row.getAttribute('data-name'));
  }

  // ───────────────────── inline preview ─────────────────────

  function getCount(sec) {
    return parseInt(sec.getAttribute('data-requests-count') || '0', 10);
  }

  function setCount(sec, count) {
    sec.setAttribute('data-requests-count', String(count));
    var badge = sec.querySelector('#message-requests-count-collapsed');
    if (badge) badge.textContent = String(count);
    // An emptied queue hides the banner rather than removing it: an undo still
    // inside its window puts the count back, and the node has to be there for
    // it to land on.
    sec.classList.toggle('hidden', count <= 0);
  }

  /** Whether the conversation pane is showing this request right now. */
  function isOpenRequest(id) {
    var barEl = document.getElementById('message-request-bar');
    if (!barEl || barEl.classList.contains('hidden')) return false;
    var activeId = window.get_active_thread_id ? String(window.get_active_thread_id()) : null;
    return barEl.getAttribute('data-thread-id') === id || activeId === id;
  }

  /**
   * Clear the conversation pane when it is showing this request. Used by the
   * removals that come back from the server, where the pane must not linger
   * behind the block-and-report modal.
   */
  function closeOpenRequest(id) {
    if (!isOpenRequest(id)) return false;

    hideRequestBar();
    // close_thread, not an innerHTML wipe: #conversation-header is a sibling of
    // #threads_right, and pm:thread-closed is what tears the call UI down.
    if (typeof window.close_thread === 'function') {
      // no_history: close_thread pushes /app/messages, which leaves Back
      // pointing at a conversation the server no longer has.
      window.close_thread(true);
    } else {
      window.abandonPagedLoad?.();
      var right = document.getElementById('threads_right');
      if (right) right.innerHTML = '';
      var main = document.getElementById('threads_main');
      if (main) main.classList.remove('threads--open');
    }
    // The address still names the thread that just went. Reloading it would
    // land on a conversation the server no longer has.
    if (window.htm5_history_supported && window.history && window.history.replaceState) {
      window.history.replaceState({}, null, '/app/messages');
    }
    return true;
  }

  /**
   * Drop a request from every surface that renders it: the conversation pane,
   * the overlay list, the peek stub in the Primary list, and the count.
   *
   * 'pm:thread-removed' also fires for ordinary Primary threads, so the count
   * moves only on evidence that this thread was a Request: a row in the list,
   * or the decision bar showing it. With no evidence the queue is left alone.
   */
  function dropRequest(id) {
    id = String(id);
    var wasOpenRequest = closeOpenRequest(id);
    var row = fvRow(id);
    if (row) row.remove();
    var undo = window.PmRequestsUndo;
    var forgotten = !!(undo && typeof undo.forgetPending === 'function' && undo.forgetPending(id));
    if (!row && !wasOpenRequest) return forgotten;

    removeThreadStub(id);
    invalidatePrefetchedView();
    fvSetCount(Math.max(0, fvGetCount() - 1));
    if (document.getElementById('message-requests-view-inner')) fvRefreshEmptyState();
    return true;
  }

  // Block from the open conversation's overflow menu removes the thread through
  // the shared controls, which fire 'pm:thread-removed' when the server call
  // completes. Report and Delete on a request row are this module's own.
  function onThreadRemoved(id) {
    dropRequest(id);
  }

  function onSectionClick(e) {
    // The section is a single banner that opens the dedicated Requests view.
    e.preventDefault();
    openFullView();
  }

  // ───────────────────── full "See all" view ─────────────────────

  // Requests-view HTML fetched during page idle so opening paints from memory
  // instead of waiting out a network round-trip (#3375). Exactly as fresh as
  // the rest of the page it was loaded with; invalidated whenever an action
  // mutates the queue, falling back to the on-click fetch.
  var prefetchedViewHtml = null;
  // Bumped on every invalidation so an in-flight prefetch that started before
  // a queue mutation cannot land its now-stale HTML afterwards.
  var prefetchGen = 0;

  function invalidatePrefetchedView() {
    prefetchedViewHtml = null;
    prefetchGen++;
  }

  function prefetchFullView() {
    if (!section() || fullView() || !viewUrl()) return;
    var gen = prefetchGen;
    fetch(viewUrl(), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (gen !== prefetchGen) return;
        prefetchedViewHtml = (html && html.trim() && isFragment(html, VIEW_MARKER)) ? html : null;
      })
      .catch(function () {
        if (gen === prefetchGen) prefetchedViewHtml = null;
      });
  }

  /**
   * Undo what createFullViewOverlay did to the pane itself. Both teardowns
   * call it: a skeleton that never became a view still positioned the pane.
   */
  function releasePanel(panel) {
    if (panel) panel.classList.remove('relative');
  }

  function createFullViewOverlay(panel) {
    panel.classList.add('relative');
    // Bump the load-more generation so an in-flight page from a previous
    // overlay (closed then reopened) can't append into or stop this fresh one.
    fvGen++;
    fvOldestCursor = '';
    var overlay = document.createElement('div');
    overlay.id = 'message-requests-view';
    overlay.className = 'absolute inset-0 z-30 bg-white dark:bg-gray-900 flex flex-col';
    panel.appendChild(overlay);
    return overlay;
  }

  function populateFullView(overlay, html, keepFocus) {
    // Replacing the markup destroys the focused node and the open-row marker,
    // neither of which the server renders. Both are read off the old DOM here
    // and put back on the new one.
    var active = document.activeElement;
    var hadFocus = !!(keepFocus && active && active.closest && overlay.contains(active));
    var focusedRow = hadFocus ? active.closest('.message-request-row') : null;
    var focusedId = focusedRow ? focusedRow.getAttribute('data-thread-id') : null;
    var openRow = overlay.querySelector('.message-request-row[data-open="true"]');
    var openId = openRow ? openRow.getAttribute('data-thread-id') : null;

    overlay.innerHTML = html;
    if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
      window.LucideIcons.initialize(overlay);
    }
    // Swipe cells ship hidden and are revealed per device. Nothing else runs
    // over these rows, so without this a touch device has no row action at all.
    if (window.SwipeActions && typeof window.SwipeActions.refresh === 'function') {
      window.SwipeActions.refresh();
    }
    watchLoadMoreSentinel();
    // Before the held rows go: the cursor has to name the oldest row the
    // server served, not the oldest one left on screen, or the next page
    // starts above rows this one already carried.
    fvNoteOldestCursor();

    // The server answers from its own state, so every view it serves carries
    // the requests whose decline is still held, and a count that includes
    // them. loadMoreRows filters a page the same way.
    fvDropHeldRows();
    var held = heldCount();
    if (held > 0) fvSetCount(Math.max(0, fvGetCount() - held));

    if (openId) markOpenRow(fvRow(openId));

    // The overlay covers the thread list it replaced, so the keyboard follows
    // it in (#3428). closeFullView hands focus back to the banner.
    if (!keepFocus) {
      var back = overlay.querySelector('#message-requests-view-back');
      if (back && typeof back.focus === 'function') back.focus();
      return;
    }
    // A refresh under the user puts the keyboard back where it was, on the same
    // row, or on the way out when that row is gone.
    if (!hadFocus) return;
    var target = (focusedId && fvRow(focusedId)) || overlay.querySelector('#message-requests-view-back');
    if (target && typeof target.focus === 'function') target.focus();
  }

  /**
   * Re-render the open overlay's first page from the server. Callers hold this
   * back while select mode or an undo window owns the rendered rows, so it
   * always runs against a list with nothing local to lose.
   */
  function reloadFullView() {
    var overlay = fullView();
    if (!overlay || !viewUrl()) return;
    // A reload issued against older state cannot answer for this one.
    if (fvReloading) { fvReloadAgain = true; return; }
    fvReloadAgain = false;
    // Paused, not removed: a reload that keeps the rendered rows keeps their
    // next page too.
    if (fvObserver) { fvObserver.disconnect(); fvObserver = null; }
    fvLoadingMore = false;
    // Bump past any load-more page still on the wire, the same guard an
    // overlay close and reopen uses.
    fvGen++;
    invalidatePrefetchedView();
    fvReloading = true;
    var gen = fvGen;
    fetch(viewUrl(), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        fvReloading = false;
        if (gen !== fvGen || !overlay.isConnected) return;
        // Rows taken out under an open undo window, or picked in select mode,
        // exist only in this DOM. Dropping the page would lose both. Announce
        // it, or nothing asks for this page again.
        if (listHeld()) { announceOverlayStale(); watchLoadMoreSentinel(); return; }
        // Not the view: keep what is rendered. The next event or refocus
        // retries, by then against a session that may be back.
        if (!isFragment(html, VIEW_MARKER)) { watchLoadMoreSentinel(); return; }
        if (!html || !html.trim()) { fvShowEmpty(); return; }
        populateFullView(overlay, html, true);
      })
      .catch(function () {
        // The list keeps what it has. The next event or refocus retries.
        fvReloading = false;
        if (gen === fvGen && overlay.isConnected) watchLoadMoreSentinel();
      })
      .then(function () {
        if (fvReloadAgain) reloadFullView();
      });
  }

  // Placeholder shown in the pane the instant the Requests view opens, while
  // the view HTML is fetched (the cold path, when the idle prefetch hasn't
  // landed or was invalidated by a queue mutation). The header carries the
  // REAL back button (same id + handler) so the overlay is always dismissable
  // even if the fetch stalls — otherwise a hung request would trap the user on
  // an opaque overlay covering the inbox. The rest is shimmer laid out on the
  // real view's boxes — same header height, same row padding and avatar size —
  // so the swap does not jog the pane.
  function skeletonHtml() {
    var row =
      '<div class="flex items-center gap-2.5 py-3 px-2">' +
        '<div class="w-[50px] h-[50px] rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>' +
        '<div class="flex-1 min-w-0 space-y-2">' +
          '<div class="h-3.5 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>' +
          '<div class="h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-800"></div>' +
        '</div>' +
      '</div>';
    var rows = '';
    for (var i = 0; i < 8; i++) rows += row;
    return '' +
      '<div class="flex flex-col h-full" aria-busy="true">' +
        '<div class="flex items-center gap-2 px-2 h-14 flex-none border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">' +
          '<button type="button" id="message-requests-view-back" ' +
                  'class="group no-btn-interactive flex h-11 w-11 flex-none items-center justify-center bg-transparent border-0 cursor-pointer rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500">' +
            '<i data-lucide="arrow-left" class="w-5 h-5 flex-shrink-0 transition-transform duration-150 group-active:scale-90 motion-reduce:transition-none motion-reduce:group-active:scale-100"></i>' +
          '</button>' +
          '<div class="min-w-0 flex-1 space-y-1.5">' +
            '<div class="h-3.5 w-32 max-w-full rounded bg-gray-200 dark:bg-gray-700"></div>' +
            '<div class="h-2.5 w-20 max-w-full rounded bg-gray-100 dark:bg-gray-800"></div>' +
          '</div>' +
          '<div class="h-11 w-16 rounded-full bg-gray-100 dark:bg-gray-800 flex-none"></div>' +
        '</div>' +
        '<div class="flex-1 flex flex-col overflow-hidden animate-pulse motion-reduce:animate-none" aria-hidden="true">' +
          '<div class="px-3 py-2.5 flex-none bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">' +
            '<div class="h-4 w-64 max-w-full rounded bg-gray-200 dark:bg-gray-700"></div>' +
          '</div>' +
          '<div class="flex-1 overflow-hidden p-2 space-y-1">' + rows + '</div>' +
        '</div>' +
      '</div>';
  }

  // Paint the skeleton into the overlay: name the back button from the section
  // banner (i18n-correct, no hardcoded copy) and initialise the Lucide arrow so
  // the functional back control renders.
  function showSkeleton(overlay) {
    overlay.innerHTML = skeletonHtml();
    var sec = section();
    var label = (sec && sec.getAttribute('data-back-label')) || '';
    if (label) {
      var btn = overlay.querySelector('#message-requests-view-back');
      if (btn) btn.setAttribute('aria-label', label);
    }
    if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
      window.LucideIcons.initialize(overlay);
    }
  }

  // ─────────────── keyset load-more (#3377) ───────────────
  // The first page renders up to page-size rows plus a sentinel; scrolling
  // near the sentinel fetches the next page of strictly-older rows
  // (?before=<last data-last-msg-id>) and appends it.
  //
  // A delete is deferred, so the server still holds a request whose row has
  // already left the DOM. The cursor therefore cannot be the rendered rows
  // alone: fvOldestCursor remembers how far the view has read, so a page
  // emptied by a bulk delete still knows where to read from, and an appended
  // row that a window is holding is dropped rather than rendered back.
  var fvOldestCursor = '';
  var fvLoadingMore = false;
  var fvReloading = false;
  var fvReloadAgain = false;
  var fvObserver = null;
  // Incremented on every overlay open (createFullViewOverlay). A load-more
  // fetch captures the current value and drops its response if the overlay was
  // closed and reopened meanwhile, so a stale page can't corrupt the new one.
  var fvGen = 0;

  function fvSentinel() {
    return document.getElementById('message-requests-view-sentinel');
  }

  /**
   * The older of two keyset cursors, either of which may be absent. Compared
   * as digit strings: a message id is past Number.MAX_SAFE_INTEGER, so a
   * numeric compare reads two neighbouring ids as equal.
   */
  function olderCursor(a, b) {
    if (!a) return b || '';
    if (!b) return a;
    if (a.length !== b.length) return a.length < b.length ? a : b;
    return a < b ? a : b;
  }

  /**
   * Reset the cursor to the freshly rendered first page. Called on a populate,
   * which throws the rest of the list away, so the deeper cursor a load-more
   * left behind would make the next page skip everything between.
   */
  function fvNoteOldestCursor() {
    var list = document.getElementById('message-requests-view-list');
    if (!list) return;
    var rows = list.querySelectorAll('.message-request-row[data-last-msg-id]');
    if (!rows.length) return;
    fvOldestCursor = rows[rows.length - 1].getAttribute('data-last-msg-id') || fvOldestCursor;
  }

  /** Whether a request is out of the view but not yet declined on the server. */
  function heldByUndo(id) {
    var undo = window.PmRequestsUndo;
    return !!(undo && typeof undo.holdsThread === 'function' && undo.holdsThread(id));
  }

  /** Say the rendered list is behind the server, so a later pass refetches it. */
  function announceOverlayStale() {
    try {
      document.dispatchEvent(new CustomEvent('pm:requests-overlay-stale'));
    } catch (e) { /* CustomEvent unavailable: nothing downstream is required */ }
  }

  /** How many requests the server still counts that this page has taken out. */
  function heldCount() {
    var undo = window.PmRequestsUndo;
    return (undo && typeof undo.heldCount === 'function') ? undo.heldCount() : 0;
  }

  /**
   * Take the rows a window is holding back out of a freshly rendered list. The
   * server answers from its own state, so every render it serves carries them.
   */
  function fvDropHeldRows() {
    var list = document.getElementById('message-requests-view-list');
    if (!list) return;
    var rows = list.querySelectorAll('.message-request-row');
    for (var i = 0; i < rows.length; i++) {
      var id = rows[i].getAttribute('data-thread-id');
      if (id && heldByUndo(id)) rows[i].remove();
    }
  }

  // Select mode styles itself from an attribute on the overlay, so an appended
  // page already looks right. Swipe still has to be turned off per row, which
  // is what these listeners are for.
  var rowsAppendedListeners = [];

  function notifyRowsAppended(rows) {
    for (var i = 0; i < rowsAppendedListeners.length; i++) {
      try {
        rowsAppendedListeners[i](rows);
      } catch (e) { /* one listener must not stop the rest */ }
    }
  }

  function fvStopLoadMore() {
    if (fvObserver) { fvObserver.disconnect(); fvObserver = null; }
    var sentinel = fvSentinel();
    if (sentinel) sentinel.remove();
  }

  function watchLoadMoreSentinel() {
    var sentinel = fvSentinel();
    if (!sentinel || typeof window.IntersectionObserver !== 'function') {
      if (sentinel) sentinel.remove();
      return;
    }
    fvObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { loadMoreRows(); break; }
      }
    }, { root: document.getElementById('message-requests-view-list'), rootMargin: '200px' });
    fvObserver.observe(sentinel);
  }

  function loadMoreRows() {
    if (fvLoadingMore) return;
    var list = document.getElementById('message-requests-view-list');
    var sentinel = fvSentinel();
    if (!list || !sentinel) return;

    var rows = list.querySelectorAll('.message-request-row[data-last-msg-id]');
    // The older of the two, not whichever exists. A delete that took the tail
    // of the last page leaves the rendered list's oldest row newer than what
    // the view has already read, and reading from it serves those pages again.
    var cursor = olderCursor(
      rows.length ? rows[rows.length - 1].getAttribute('data-last-msg-id') : '',
      fvOldestCursor
    );
    if (!cursor || !viewUrl()) {
      fvStopLoadMore();
      if (!list.querySelector('.message-request-row')) fvShowEmpty();
      return;
    }

    fvLoadingMore = true;
    var gen = fvGen;
    fetch(viewUrl() + '?before=' + encodeURIComponent(cursor), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (gen !== fvGen) return; // overlay was closed/reopened; drop stale page
        fvLoadingMore = false;
        if (!fvSentinel()) return; // view closed or already stopped
        // Not a rows page: leave the sentinel, the way a failed fetch does.
        // Treating it as the end of the list would show the empty state.
        if (!isFragment(html, ROWS_MARKER)) return;
        if (!html || !html.trim()) {
          fvStopLoadMore();
          if (!list.querySelector('.message-request-row')) fvShowEmpty();
          return;
        }
        var frag = document.createElement('template');
        frag.innerHTML = html;
        var served = Array.prototype.slice.call(frag.content.querySelectorAll('.message-request-row'));
        // The server answers from its own state, so a page can carry a request
        // this view has already removed: one an open window still holds, or one
        // a refresh has rendered above. Either way it must not appear twice.
        var appended = [];
        for (var i = 0; i < served.length; i++) {
          var servedId = served[i].getAttribute('data-thread-id');
          if (servedId && (fvRow(servedId) || heldByUndo(servedId))) {
            served[i].remove();
            continue;
          }
          appended.push(served[i]);
        }
        sentinel.before(frag.content);
        if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
          window.LucideIcons.initialize(list);
        }
        // Swipe cells ship hidden and are revealed per device, so rows that
        // never went through a list rebuild need this to be touchable at all.
        if (window.SwipeActions && typeof window.SwipeActions.refresh === 'function') {
          window.SwipeActions.refresh();
        }
        // Read off what the server served, not off the DOM: a page filtered
        // down to nothing still has to move the cursor, or the next read asks
        // for the same rows again.
        var lastServed = served.length
          ? served[served.length - 1].getAttribute('data-last-msg-id')
          : '';
        if (lastServed) fvOldestCursor = lastServed;
        if (appended.length) fvHideEmpty();
        notifyRowsAppended(appended);
        var pageSize = parseInt(list.getAttribute('data-page-size') || '0', 10);
        // Measured on what the server served, not on what was kept: a page
        // filtered down to nothing still means more pages follow.
        if (!served.length || (pageSize > 0 && served.length < pageSize)) {
          fvStopLoadMore();
          return;
        }
        // Nothing was inserted, so the sentinel did not move and the observer
        // will not fire again. Ask for the next page, but only once the cursor
        // has moved: repeating the same read would never end.
        if (!appended.length && lastServed && lastServed !== cursor) loadMoreRows();
      })
      .catch(function () {
        if (gen !== fvGen) return; // stale page from a since-closed overlay
        fvLoadingMore = false;
        // Leave the sentinel: scrolling again retries the page.
      });
  }

  /**
   * The overlay covers the pane's other children but does not remove them, so
   * their controls stay in the tab order behind it. `inert` takes them out for
   * as long as it is up (#3428).
   */
  function setPanelInert(panel, overlay, inert) {
    var kids = panel.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i] === overlay) continue;
      if (inert) {
        kids[i].setAttribute('inert', '');
      } else {
        kids[i].removeAttribute('inert');
      }
    }
  }

  /**
   * Abandon an overlay that never got its content. A skeleton has no observer,
   * sentinel or mutated count, so it just goes — but the keyboard is on it, and
   * the control that opened it was unfocused by inert, so focus is handed back.
   */
  function dropSkeleton(panel, overlay) {
    setPanelInert(panel, overlay, false);
    overlay.remove();
    releasePanel(panel);
    var opener = document.getElementById('message-requests-open');
    if (opener && opener.offsetParent !== null && typeof opener.focus === 'function') {
      opener.focus();
    }
  }

  function openFullView() {
    // Fill the threads (left) pane, not the conversation pane — the Requests
    // view replaces the list in place and works full-screen on mobile.
    var panel = document.getElementById('threads_left_container');
    if (!panel || fullView() || !viewUrl()) return;

    var overlay = createFullViewOverlay(panel);
    setPanelInert(panel, overlay, true);

    if (prefetchedViewHtml) {
      populateFullView(overlay, prefetchedViewHtml);
      return;
    }

    // Cold path: paint a skeleton at once so the pane responds to the tap while
    // the view HTML is fetched, then swap in the real content when it lands.
    showSkeleton(overlay);
    // inert on the pane unfocuses whatever held the keyboard, and the control
    // that opened this overlay is inside it. The skeleton takes the keyboard so
    // the fetch does not run with focus on the body (#3428).
    overlay.setAttribute('tabindex', '-1');
    overlay.focus();
    fetch(viewUrl(), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (!overlay.isConnected) return; // view closed while loading
        // Empty view: a skeleton overlay has no observer, sentinel, or mutated
        // count, so drop it directly — no closeFullView bookkeeping needed.
        // A body that is not the view is treated the same way.
        if (!isFragment(html, VIEW_MARKER) || !html.trim()) {
          dropSkeleton(panel, overlay);
          return;
        }
        populateFullView(overlay, html);
      })
      .catch(function () {
        if (!overlay.isConnected) return;
        dropSkeleton(panel, overlay);
        actionError();
      });
  }

  function closeFullView() {
    // fvSetCount keeps the section banner count in sync while the view is
    // open, so closing needs no reload. An emptied banner hides rather than
    // going away: a commit that fails after this point restores the count, and
    // the node has to still be there to take it.
    if (window.PmRequestsUndo && typeof window.PmRequestsUndo.commitNow === 'function') {
      window.PmRequestsUndo.commitNow();
    }
    if (window.PmRequestsSelect && typeof window.PmRequestsSelect.exit === 'function') {
      window.PmRequestsSelect.exit();
    }
    if (fvObserver) { fvObserver.disconnect(); fvObserver = null; }
    fvLoadingMore = false;
    fvReloading = false;
    fvReloadAgain = false;
    var overlay = fullView();
    var panel = document.getElementById('threads_left_container');
    if (overlay) {
      if (panel) setPanelInert(panel, overlay, false);
      overlay.remove();
    }
    releasePanel(panel);
    // Focus returns to the control that opened the overlay (#3428). An emptied
    // queue hides that control, so the list it uncovered takes the keyboard
    // instead of dropping it on the body.
    var opener = document.getElementById('message-requests-open');
    if (opener && opener.offsetParent !== null && typeof opener.focus === 'function') {
      opener.focus();
      return;
    }
    var list = document.getElementById('threads_left');
    if (list && typeof list.focus === 'function') {
      list.setAttribute('tabindex', '-1');
      list.focus();
    }
  }

  // With the overlay closed the banner is the only count on the page, and a
  // delete from the open conversation runs in exactly that state.
  function fvGetCount() {
    var inner = document.getElementById('message-requests-view-inner');
    if (inner) return parseInt(inner.getAttribute('data-requests-count') || '0', 10);
    var sec = section();
    return sec ? getCount(sec) : 0;
  }

  // Plural selection for the header count line. tPlural reads the pipe-separated
  // forms a locale may carry; a catalogue with one form falls through unchanged.
  function pendingCountText(count) {
    if (typeof window.tPlural === 'function') {
      var s = window.tPlural('legacy.pm.requests_pending_count', count);
      if (s && s !== 'legacy.pm.requests_pending_count') return s;
    }
    return count + ' pending';
  }

  function fvSetCount(count) {
    var inner = document.getElementById('message-requests-view-inner');
    if (inner) inner.setAttribute('data-requests-count', String(count));
    var line = document.getElementById('message-requests-view-count');
    // Select mode owns this line while it is on, so a count arriving from the
    // server must not overwrite the selection count showing there.
    var header = !selectMode();
    if (line && header) {
      // Unhidden first. A live region announces nothing while it is display
      // none, and revealing it afterwards does not replay the text.
      line.classList.toggle('hidden', count <= 0);
      line.textContent = pendingCountText(count);
    }
    // An empty queue has nothing to select, so the entry to select mode goes
    // with the count line.
    var selectBtn = document.getElementById('message-requests-view-select');
    if (selectBtn && header) selectBtn.classList.toggle('hidden', count <= 0);
    var sec = section();
    if (sec) setCount(sec, count);
  }

  function fvShowEmpty() {
    fvStopLoadMore();
    if (window.PmRequestsSelect && typeof window.PmRequestsSelect.exit === 'function') {
      window.PmRequestsSelect.exit();
    }
    var list = document.getElementById('message-requests-view-list');
    var empty = document.getElementById('message-requests-view-empty');
    if (list) list.classList.add('hidden');
    if (empty) empty.classList.remove('hidden');
    // The header keeps only the title and the way back: there is nothing left
    // to count or to select. A stale server count must not put them back.
    var line = document.getElementById('message-requests-view-count');
    if (line) line.classList.add('hidden');
    var selectBtn = document.getElementById('message-requests-view-select');
    if (selectBtn) selectBtn.classList.add('hidden');
  }

  function fvHideEmpty() {
    var list = document.getElementById('message-requests-view-list');
    var empty = document.getElementById('message-requests-view-empty');
    if (list) list.classList.remove('hidden');
    if (empty) empty.classList.add('hidden');
  }

  function fvRows() {
    var list = document.getElementById('message-requests-view-list');
    return list ? Array.prototype.slice.call(list.querySelectorAll('.message-request-row')) : [];
  }

  function fvRow(threadId) {
    var list = document.getElementById('message-requests-view-list');
    if (!list) return null;
    // Scanned rather than selected: an id also arrives on a websocket frame,
    // and a value carrying a quote would make a built selector throw.
    var id = String(threadId);
    var rows = list.querySelectorAll('.message-request-row');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-thread-id') === id) return rows[i];
    }
    return null;
  }

  /** Compare two keyset cursors, newest first. They exceed Number.MAX_SAFE_INTEGER. */
  function cursorIsOlder(a, b) {
    if (a.length !== b.length) return a.length < b.length;
    return a < b;
  }

  /**
   * Take a row out of the list and return what restoring it needs. The cursor
   * is kept rather than the sibling the row preceded: that sibling can itself
   * be detached by the same batch, or pushed down the list by a keyset page
   * appended while the undo window is open.
   */
  function fvDetachRow(threadId) {
    var row = fvRow(threadId);
    if (!row) return null;
    var token = { row: row, cursor: row.getAttribute('data-last-msg-id') || '' };
    row.remove();
    // A detached row keeps whatever it was wearing. Restored later it would
    // put a second accent bar next to the row the pane is really showing.
    row.removeAttribute('data-open');
    var opener = row.querySelector('.message-request-open');
    if (opener) opener.removeAttribute('aria-current');
    return token;
  }

  /**
   * Put a row back in cursor order. The list is always newest first, so the
   * row goes before the first row older than it, and ahead of the load-more
   * sentinel when it is the oldest one rendered.
   */
  function fvRestoreRow(token) {
    var list = document.getElementById('message-requests-view-list');
    if (!token || !token.row || !list) return false;
    // A refresh landing inside the window renders the request back from the
    // server. Inserting the detached node on top of it would show one request
    // as two rows and count it twice.
    var id = token.row.getAttribute('data-thread-id');
    if (id && fvRow(id)) return false;

    var before = null;
    var rows = list.querySelectorAll('.message-request-row');
    for (var i = 0; i < rows.length; i++) {
      if (cursorIsOlder(rows[i].getAttribute('data-last-msg-id') || '', token.cursor)) {
        before = rows[i];
        break;
      }
    }
    list.insertBefore(token.row, before || fvSentinel());
    return true;
  }

  // Empty vs populated after rows were removed: pull the next keyset page when
  // one is still pending, so a page that emptied itself refills instead of
  // claiming the queue is done.
  function fvRefreshEmptyState() {
    var list = document.getElementById('message-requests-view-list');
    if (!list) return;
    if (list.querySelector('.message-request-row')) {
      fvHideEmpty();
      return;
    }
    if (fvSentinel()) {
      loadMoreRows();
      return;
    }
    fvShowEmpty();
  }

  // Keep the keyboard in the list after a row goes away. Returns where it put
  // the focus, so a caller that borrows it can hand it back.
  function fvFocusAfterRemoval(nextRow, prevRow) {
    var row = (nextRow && nextRow.isConnected) ? nextRow
      : ((prevRow && prevRow.isConnected) ? prevRow : null);
    // The row is a listitem and takes no focus. Its open button does.
    var target = row ? (row.querySelector('.message-request-open') || row) : null;
    // With no neighbour the overlay's Back button takes it, and with no overlay
    // the banner that opens one does. A delete from the conversation runs in
    // exactly that second state.
    if (!target || target.offsetParent === null) {
      target = document.getElementById('message-requests-view-back');
    }
    // An emptied queue hides the banner, so the list it uncovered takes the
    // keyboard rather than the body getting it.
    if (!target || target.offsetParent === null) {
      target = document.getElementById('message-requests-open');
    }
    if (!target || target.offsetParent === null) {
      target = document.getElementById('threads_left');
      if (target) target.setAttribute('tabindex', '-1');
    }
    if (!target || typeof target.focus !== 'function') return null;
    target.focus();
    return target;
  }

  // ─────────────── row actions ───────────────

  function selectMode() {
    return !!(window.PmRequestsSelect && window.PmRequestsSelect.isActive());
  }

  /**
   * Whether the rendered list holds state the server cannot supply: a
   * selection, or rows whose delete can still be undone. Checked both before a
   * refresh is issued and again when its response lands.
   */
  function listHeld() {
    if (selectMode()) return true;
    var undo = window.PmRequestsUndo;
    return !!(undo && typeof undo.heldCount === 'function' && undo.heldCount() > 0);
  }

  function reportRow(row) {
    var uid = row.getAttribute('data-uid');
    if (!uid) return;
    if (window.SwipeActions) window.SwipeActions.closeAll();
    if (typeof window.navigate_to_report === 'function') {
      window.navigate_to_report(uid);
    }
  }

  function deleteRow(row, fromKeyboard) {
    if (window.SwipeActions) window.SwipeActions.closeAll();
    if (window.PmRequestsUndo && typeof window.PmRequestsUndo.scheduleSingle === 'function') {
      window.PmRequestsUndo.scheduleSingle(row, fromKeyboard);
    }
  }

  // A button pressed with Enter or Space reports detail 0, a real pointer click
  // reports 1 or more. Tells the undo window whether to hand over the keyboard.
  function isKeyboardClick(e) {
    return e.detail === 0;
  }

  /**
   * Leave the conversation if it is showing one of these requests. Called by
   * the undo module before it takes the rows out, so a delete from the list
   * cannot leave a decision bar up for a request it just removed, nor let a
   * second Delete schedule the same decline twice.
   */
  function closeOpenRequests(ids) {
    for (var i = 0; i < ids.length; i++) {
      if (closeOpenRequest(String(ids[i]))) return;
    }
  }

  function onFullViewClick(e) {
    // Above every action: swipe-actions.js holds isDragging past touchend, so
    // the click a gesture leaves behind must not act on the row it revealed.
    if (window.isDragging) { e.preventDefault(); return; }

    if (e.target.closest('#message-requests-view-back')) {
      e.preventDefault();
      closeFullView();
      return;
    }
    if (e.target.closest('#message-requests-view-select')) {
      e.preventDefault();
      if (window.PmRequestsSelect) window.PmRequestsSelect.toggle();
      return;
    }
    if (e.target.closest('#message-requests-view-delete-selected')) {
      e.preventDefault();
      if (window.PmRequestsSelect) window.PmRequestsSelect.deleteSelected(isKeyboardClick(e));
      return;
    }

    var row = e.target.closest('.message-request-row');
    if (!row) return;

    if (e.target.closest('.message-request-report')) {
      e.preventDefault();
      reportRow(row);
      return;
    }
    if (e.target.closest('.message-request-delete')) {
      e.preventDefault();
      deleteRow(row, isKeyboardClick(e));
      return;
    }
    // The swipe panel dispatches its own event; a click inside it is not a
    // row-open.
    if (e.target.closest('.thread-swipe-actions')) return;

    if (selectMode()) {
      e.preventDefault();
      window.PmRequestsSelect.toggleRow(row);
      return;
    }

    var avatar = e.target.closest('.request-avatar');
    if (avatar) {
      // The photo is an anchor to the profile, and the browser follows it. A
      // row without a profile keeps the row behaviour, and its '#' href would
      // push a history entry over the one openRequestPeek writes.
      if (window.pm_is_profile_href(avatar.getAttribute('href'))) return;
      e.preventDefault();
    }

    // Row body opens the conversation in-page (the full view stays open in the
    // threads pane on desktop, and is covered by the conversation on mobile).
    openRequestPeek(row);
  }

  // ───────────────────── the open request's decision bar ─────────────────────

  /**
   * One line at the end of the conversation marking the moment the request was
   * accepted. Client-side only: nothing persists it, so it is gone on the next
   * load, which is what a confirmation of an action you just took should do.
   */
  function appendAcceptedNote(name) {
    var wrapper = document.getElementById('messages_wrapper');
    if (!wrapper) return;
    // The only wording available names the sender. With no name it reads as a
    // sentence with a hole in it, so say nothing instead.
    if (!name) return;
    var note = document.createElement('div');
    note.className = 'pm-request-accepted-note mt-3 mb-1 flex justify-center px-4';
    note.setAttribute('role', 'status');
    var text = document.createElement('p');
    text.className = 'max-w-[84%] rounded-full bg-gray-100 px-3 py-1.5 text-center text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300';
    note.appendChild(text);
    // Paged view keeps its page control after the last message, so stay above it.
    var controls = document.getElementById('paged-view-controls');
    if (controls && controls.parentNode === wrapper) {
      wrapper.insertBefore(note, controls);
    } else {
      wrapper.appendChild(note);
    }
    // Text after the insert: a live region announces what arrives once it is
    // already in the tree, never the content it was built holding.
    text.textContent = t(
      'legacy.pm.request_accepted_note',
      'You accepted the request. ' + name + ' can see your replies now.',
      { name: name }
    );
  }

  /**
   * Rebuild the Primary list now. A box move carries no message, so the poll's
   * own rebuild test cannot see one, and the forcing argument is what makes it
   * read the list again.
   */
  function forcePoll() {
    var poll = window.pm_view_mode === 'paged' ? window.update_pm_info_paged : window.update_pm_info;
    if (typeof poll === 'function') poll(true, 'event');
  }

  /**
   * Take a thread out of the Requests queue because it is now Primary. Drives
   * both the local accept and a move that happened on another device, which is
   * why the conversation pane is only touched when it is showing that request.
   *
   * The poll at the end does three jobs the accepted thread needs: it brings
   * the Primary row in, marks that row active, and marks the thread read.
   *
   * `local` marks the accept this user just made: only then does the pane get
   * the confirming line and the keyboard.
   */
  function applyAccepted(id, name, local) {
    // An accept of a thread whose delete is still inside its undo window wins:
    // the decline must never be sent for a request the user just took.
    if (window.PmRequestsUndo && typeof window.PmRequestsUndo.cancelPending === 'function') {
      window.PmRequestsUndo.cancelPending(id);
    }

    var bar = document.getElementById('message-request-bar');
    var wasOpenRequest = !!(bar && !bar.classList.contains('hidden') &&
      String(bar.getAttribute('data-thread-id')) === String(id));
    if (wasOpenRequest) {
      hideRequestBar();

      var tr = document.getElementById('threads_right');
      if (tr) tr.classList.remove('pm-request-readonly');

      // An inactive contact takes no message, accepted or not. view_thread
      // reads the same flag off this row to keep the composer hidden.
      var carried = document.getElementById('thread_' + id);
      var inactive = !!(carried && carried.getAttribute('data-inactive'));
      var compose = document.getElementById('threads_right_bottom');
      if (compose && !inactive) compose.classList.remove('hidden');

      if (local) {
        appendAcceptedNote(name);
        var input = document.getElementById('message');
        if (!inactive && input && typeof input.focus === 'function') input.focus();
      }
    }

    // Drop the row and the count wherever they are rendered: the overlay list
    // when it is open, the banner either way. The count moves only on evidence
    // that this thread was in the queue, so a redelivered move frame cannot
    // walk it down a second time.
    var row = fvRow(id);
    var wasOpenRow = !!(row && row.getAttribute('data-open') === 'true');
    if (row) row.remove();
    if (row || wasOpenRequest) fvSetCount(Math.max(0, fvGetCount() - 1));
    removeThreadStub(id);
    // Only when this thread was the marked one: a move frame for some other
    // thread must not take the accent off the request being read.
    if (wasOpenRow || wasOpenRequest) markOpenRow(null);
    fvRefreshEmptyState();
    invalidatePrefetchedView();

    forcePoll();
  }

  // Accepting raises a promotion frame that reaches this tab too, and it can
  // win the race against the POST response. The frame is claimed here so only
  // the local pass applies the accept, which is the one that knows to confirm
  // it in the conversation.
  var selfAccepted = Object.create(null);
  // A claim whose frame never arrives (socket down) must not swallow a later,
  // unrelated move of the same thread.
  var SELF_ACCEPT_CLAIM_MS = 30000;
  // Each accept owns its claim, so an expiry never removes a later one.
  var selfAcceptSeq = 0;

  function acceptOpenRequest(bar, id) {
    var key = String(id);
    var name = openRequestName(bar);
    setBarBusy(bar, true);
    var claim = ++selfAcceptSeq;
    selfAccepted[key] = claim;
    var applied = false;
    function landed() {
      if (applied) return;
      applied = true;
      applyAccepted(id, name, true);
      setTimeout(function () {
        if (selfAccepted[key] === claim) delete selfAccepted[key];
      }, SELF_ACCEPT_CLAIM_MS);
      // After the local subtraction: a banner fetched before it would
      // already exclude this request and take it off a second time.
      document.dispatchEvent(new CustomEvent('pm:requests-accept-settled'));
    }
    apiPost('/thread/' + id + '/accept')
      .then(landed)
      .catch(function () {
        // A consumed claim means the promotion frame arrived, so the server
        // applied the accept and only its response was lost.
        if (selfAccepted[key] !== claim) { landed(); return; }
        delete selfAccepted[key];
        setBarBusy(bar, false);
        actionError();
      });
  }

  /**
   * Delete the open request. No confirm dialog: the undo window is the
   * safeguard, the same one the list uses. The conversation closes first,
   * because the request it shows is already out of the queue.
   */
  function deleteOpenRequest(bar, id, fromKeyboard) {
    if (!window.PmRequestsUndo || typeof window.PmRequestsUndo.scheduleThread !== 'function') return;
    var name = openRequestName(bar);
    // The same guarded close the server-driven removal takes: it leaves no
    // history entry naming a conversation that is going away, and the carried
    // row goes with it, or popstate reads the request as still listed.
    closeOpenRequest(id);
    removeThreadStub(id);
    // The conversation is already gone from the page, so a schedule that opens
    // no window has to say so. Silence here reads as a delete that worked.
    if (!window.PmRequestsUndo.scheduleThread({ id: id, name: name, fromKeyboard: fromKeyboard })) {
      actionError();
    }
  }

  function onBarClick(e, bar) {
    var id = bar.getAttribute('data-thread-id');
    if (!id) return;

    if (e.target.closest('#message-request-bar-accept')) {
      e.preventDefault();
      acceptOpenRequest(bar, id);
      return;
    }
    if (e.target.closest('#message-request-bar-delete')) {
      e.preventDefault();
      deleteOpenRequest(bar, id, isKeyboardClick(e));
    }
  }

  /**
   * Escape leaves the peeked conversation's menu first, then select mode, then
   * the overlay, so one key never discards two surfaces at once. Scoped to
   * events raised inside the overlay, so a dialog stacked above it keeps its
   * own Escape. The conversation menu carries none of its own.
   */
  function onKeydown(e) {
    var fv = fullView();
    if (!fv) return;
    // document.body counts as inside: the overlay owns the pane, and focus
    // lands on the body whenever a node it held goes away. A dialog stacked
    // above sets aria-modal, which is the signal that Escape is not ours.
    // Tested by client rects, and only for a dialog that is showing: several
    // sit in the page markup at all times, hidden, and they are position:fixed,
    // whose offsetParent is null whether they show or not.
    var modals = document.querySelectorAll('[aria-modal="true"]');
    for (var m = 0; m < modals.length; m++) {
      if (modals[m].getClientRects().length && !fv.contains(modals[m])) return;
    }
    // The undo toast also counts: a keyboard delete hands it the focus, and
    // it sits at the end of the body rather than inside the overlay.
    var inToast = !!(e.target.closest && e.target.closest('#notification-action-container'));
    var inOverlay = e.target === document.body || (e.target.closest && fv.contains(e.target));
    if (!inOverlay && !inToast) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      var menu = document.getElementById('conversation-actions-menu');
      if (menu && !menu.classList.contains('hidden')) {
        menu.classList.add('hidden');
        return;
      }
      if (selectMode()) {
        window.PmRequestsSelect.exit();
      } else {
        closeFullView();
      }
    }
  }

  function onClick(e) {
    var bar = document.getElementById('message-request-bar');
    if (bar && bar.contains(e.target)) {
      onBarClick(e, bar);
      return;
    }
    var fv = fullView();
    if (fv && fv.contains(e.target)) {
      onFullViewClick(e);
      return;
    }
    var sec = section();
    if (sec && sec.contains(e.target)) {
      onSectionClick(e);
    }
  }

  // The settings validator reads form-encoded POST parameters.
  // Reload after the update to render the inbox in the selected mode.
  window.toggle_message_requests = function (enable) {
    if (!window.ApiClient || typeof window.ApiClient.request !== 'function') {
      return false;
    }

    var userId = window.pmCurrentUserId();
    if (!userId) {
      return false;
    }

    window.ApiClient.request('/settings/' + userId, {
      method: 'PUT',
      body: new URLSearchParams({ message_requests: enable ? 'true' : 'false' })
    }).then(function (resp) {
      if (!resp || !resp.ok) {
        throw new Error('Request failed: ' + (resp ? resp.status : 'no response'));
      }
      window.location.reload();
    }).catch(function () {
      actionError();
    });

    return false;
  };

  function init() {
    // A direct ?thread_id= load renders the bar server-side, with no peek to
    // put the menu in request mode.
    syncThreadMenu();
    // Warm the full-view HTML once the page is idle, so tapping the banner
    // paints from memory. Only when there is a Requests section to open.
    if (section()) {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(prefetchFullView, { timeout: 3000 });
      } else {
        window.setTimeout(prefetchFullView, 1500);
      }
    }
  }

  /**
   * List surface the select and undo modules drive. Everything that mutates the
   * rendered queue goes through here, so the count, the empty state and the
   * prefetched-HTML invalidation stay in one place.
   */
  window.PmRequests = {
    overlay: fullView,
    inner: function () { return document.getElementById('message-requests-view-inner'); },
    list: function () { return document.getElementById('message-requests-view-list'); },
    rows: fvRows,
    row: fvRow,
    getCount: fvGetCount,
    setCount: fvSetCount,
    detachRow: fvDetachRow,
    restoreRow: fvRestoreRow,
    refreshEmptyState: fvRefreshEmptyState,
    focusAfterRemoval: fvFocusAfterRemoval,
    reloadOverlay: reloadFullView,
    /**
     * Whether the rendered list is the whole queue. The view is keyset
     * paginated, so a missing row means "not on a loaded page" until the
     * load-more sentinel is gone. False while a reload is out too: the reload
     * can replace the list, so no absent row is final until it lands.
     */
    listComplete: function () {
      return !fvReloading
        && !!document.getElementById('message-requests-view-list')
        && !fvSentinel();
    },
    listHeld: listHeld,
    closeOpenRequests: closeOpenRequests,

    /**
     * Point the decision bar at the request a history entry opened. view_thread
     * shows the bar for a carried row, but the bar still names the sender of
     * the last request it held.
     */
    restoreRequestBar: function (threadId) {
      var id = String(threadId);
      var src = fvRow(id);
      if (!src) {
        var stub = document.getElementById('thread_' + id);
        if (stub && stub.closest('#pm-active-request-row')) src = stub;
      }
      if (!src) return false;
      showRequestBar(id, src.getAttribute('data-uid'), src.getAttribute('data-name'));
      // Only when the row is rendered. A null here would take the marker off
      // the row the pane is showing, which sits on a page not loaded yet.
      var row = fvRow(id);
      if (row) markOpenRow(row);
      return true;
    },
    /**
     * A thread that left the Requests queue for Primary, elsewhere. True when
     * the frame is this tab's own accept, which refetches the banner itself
     * once its POST lands.
     */
    applyMovedToPrimary: function (threadId) {
      var id = String(threadId);
      if (selfAccepted[id]) {
        delete selfAccepted[id];
        return true;
      }
      applyAccepted(id, '', false);
      return false;
    },
    /**
     * A thread demoted back to Requests elsewhere. The mirror of
     * applyMovedToPrimary: the Primary row goes, the poll runs, and when the
     * thread is the open conversation the decision bar and the no-receipt
     * gating come back. Replying to an unaccepted request is what they prevent.
     */
    applyMovedToRequests: function (threadId) {
      var id = String(threadId);

      // The Primary row leaves the list whether or not this is the open
      // conversation. It is carried, not removed: view_thread and
      // restoreRequestBar read a carried row as a pending request, and a reply
      // is an implicit accept server-side.
      var primary = document.getElementById('thread_' + id);
      if (primary && primary.closest('#threads_left')) carrier().appendChild(primary);

      var activeId = window.get_active_thread_id ? String(window.get_active_thread_id()) : null;
      if (activeId === id) {
        var row = fvRow(id) || primary;
        showRequestBar(
          id,
          row ? row.getAttribute('data-uid') : '',
          row ? row.getAttribute('data-name') : ''
        );
      }

      forcePoll();
    },
    onRowsAppended: function (cb) {
      if (typeof cb === 'function') rowsAppendedListeners.push(cb);
    },
    // A mutated queue makes the idle-prefetched view HTML stale.
    markChanged: invalidatePrefetchedView,
    apiPost: apiPost,
    actionError: actionError
  };

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  // Swipe cells on a request row are ours: messages.js handles .pm_thread only.
  document.addEventListener('swipe-action', function (e) {
    var detail = e.detail;
    if (!detail || !detail.row) return;
    var row = detail.row;
    if (!row.classList || !row.classList.contains('message-request-row')) return;
    if (detail.action === 'report') {
      reportRow(row);
    } else if (detail.action === 'delete') {
      deleteRow(row);
    }
  });
  // Block from the open conversation's menu goes through the shared thread
  // controls; sync the request list + count when it completes.
  document.addEventListener('pm:thread-removed', function (e) {
    onThreadRemoved(e.detail && e.detail.threadId);
  });
  // view_thread sets the bar for a carried request row before pm:thread-opened
  // fires, so the menu lands on the right set.
  document.addEventListener('pm:thread-opened', syncThreadMenu);
  document.addEventListener('pm:thread-closed', syncThreadMenu);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
